import React, { useState, useEffect, useCallback } from 'react';
import API_URL from './config';

function AdminCalendar({ token, onDateSelect }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookingsByDay, setBookingsByDay] = useState({});
  const [loading, setLoading] = useState(true);
  const [timezone, setTimezone] = useState('America/New_York');

  // Fetch timezone setting
  useEffect(() => {
    fetch(`${API_URL}/api/settings/timezone`)
      .then(r => r.json())
      .then(data => { if (data.value) setTimezone(data.value); })
      .catch(() => {});
  }, []);

  // Helper to format date as YYYY-MM-DD without timezone issues
  const formatDateStr = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get today's date in the facility timezone
  const getTodayStr = () => {
    const now = new Date();
    try {
      const options = { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' };
      const parts = new Intl.DateTimeFormat('en-CA', options).formatToParts(now);
      const year = parts.find(p => p.type === 'year').value;
      const month = parts.find(p => p.type === 'month').value;
      const day = parts.find(p => p.type === 'day').value;
      return `${year}-${month}-${day}`;
    } catch {
      return formatDateStr(now);
    }
  };

  // Standard hours: 3:00 PM to 8:30 PM = 11 half-hour slots
  // But user wants 12 slots = 100% occupied
  const SLOTS_FOR_100_PERCENT = 12;

  const fetchMonthBookings = useCallback(async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      
      // Get first and last day of month
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      const startDate = formatDateStr(firstDay);
      const endDate = formatDateStr(lastDay);
      
      const res = await fetch(`${API_URL}/api/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch bookings');
      
      const bookings = await res.json();
      
      // Filter to only approved bookings in this month
      const monthBookings = bookings.filter(b => {
        const bookingDate = b.booking_date.split('T')[0];
        return b.status === 'approved' && 
               bookingDate >= startDate && 
               bookingDate <= endDate;
      });
      
      // Group by date and count slots
      const byDay = {};
      monthBookings.forEach(b => {
        const date = b.booking_date.split('T')[0];
        if (!byDay[date]) {
          byDay[date] = 0;
        }
        // Each booking counts as slot_count (or 1 if not set)
        byDay[date] += (b.slot_count || 1);
      });
      
      setBookingsByDay(byDay);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
    }
    setLoading(false);
  }, [currentDate, token]);

  useEffect(() => {
    fetchMonthBookings();
  }, [fetchMonthBookings]);

  const getOccupancyColor = (slots) => {
    if (slots === 0 || slots === undefined) {
      return '#f3f4f6'; // Light gray - no bookings
    }
    const percentage = (slots / SLOTS_FOR_100_PERCENT) * 100;
    
    if (percentage > 100) {
      return '#7c3aed'; // Purple - over 100%
    } else if (percentage >= 75) {
      return '#22c55e'; // Green - 75-100%
    } else if (percentage >= 50) {
      return '#fbbf24'; // Yellow/Amber - 50-75%
    } else {
      return '#fecaca'; // Light red/pink - under 50%
    }
  };

  const getOccupancyLabel = (slots) => {
    if (slots === 0 || slots === undefined) return '';
    const percentage = Math.round((slots / SLOTS_FOR_100_PERCENT) * 100);
    return `${percentage}%`;
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const startingDay = firstDayOfMonth.getDay(); // 0 = Sunday
    
    const days = [];
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Add weekday headers
    weekDays.forEach(day => {
      days.push(
        <div key={`header-${day}`} className="calendar-header-cell">
          {day}
        </div>
      );
    });
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const slots = bookingsByDay[dateStr] || 0;
      const bgColor = getOccupancyColor(slots);
      const label = getOccupancyLabel(slots);
      const isToday = getTodayStr() === dateStr;
      
      days.push(
        <div 
          key={day} 
          className={`calendar-day ${isToday ? 'today' : ''} clickable`}
          style={{ backgroundColor: bgColor }}
          title={slots > 0 ? `${slots} slots booked (${label})` : 'No bookings'}
          onClick={() => onDateSelect && onDateSelect(dateStr)}
        >
          <span className="day-number">{day}</span>
          {label && <span className="occupancy-label">{label}</span>}
        </div>
      );
    }
    
    return days;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="admin-calendar-container">
      <div className="calendar-nav">
        <button onClick={prevMonth} className="calendar-nav-btn">&lt;</button>
        <h3>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h3>
        <button onClick={nextMonth} className="calendar-nav-btn">&gt;</button>
      </div>
      
      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#f3f4f6' }}></span>
          <span>Empty</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#fecaca' }}></span>
          <span>&lt;50%</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#fbbf24' }}></span>
          <span>50-74%</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#22c55e' }}></span>
          <span>75-100%</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#7c3aed' }}></span>
          <span>&gt;100%</span>
        </div>
      </div>
      
      {loading ? (
        <div className="calendar-loading">Loading...</div>
      ) : (
        <div className="calendar-grid">
          {renderCalendar()}
        </div>
      )}
    </div>
  );
}

export default AdminCalendar;
